import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  longTextSchema,
  shortTextSchema,
  yesNoSchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import { getWorkflowTranslationContext } from "../../workflowLogic/helpers/getWorkflowTranslationContext.js";

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "didYouLeave",
    type: "radio",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("leavePlaceOfSafety.didYouLeave"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    requiredLabel: ({ incidentWorkItem }) => ({
      translationKey: tk("leavePlaceOfSafety.didYouLeaveRequired"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    options: [
      { label: tk("common.yes"), value: "yes" },
      { label: tk("common.no"), value: "no" },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "leaveReason",
    type: "dropdown",
    label: tk("leavePlaceOfSafety.leaveReason.title"),
    requiredLabel: tk("leavePlaceOfSafety.leaveReason.required"),
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "moreAppropriatePosAvailable" as const,
          "unreasonableDelay" as const,
          "visitAccidentAndEmergencyOnMedicalGrounds" as const,
          "physicalHealthCheckComplete" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: ({ incidentWorkItem }) =>
      [
        {
          label: tk(
            "leavePlaceOfSafety.leaveReason.options.moreAppropriatePosAvailable",
          ),
          value: "moreAppropriatePosAvailable",
        },
        {
          label: tk("leavePlaceOfSafety.leaveReason.options.unreasonableDelay"),
          value: "unreasonableDelay",
        },
        {
          label: tk(
            "leavePlaceOfSafety.leaveReason.options.visitAccidentAndEmergencyOnMedicalGrounds",
          ),
          value: "visitAccidentAndEmergencyOnMedicalGrounds",
        },
        {
          label: tk(
            "leavePlaceOfSafety.leaveReason.options.physicalHealthCheckComplete",
          ),
          value: "physicalHealthCheckComplete",
        },
        {
          label: tk("leavePlaceOfSafety.leaveReason.options.other"),
          value: "other",
        },
      ].map((option) => ({
        label: {
          translationKey: option.label,
          translationOptions: {
            context: getWorkflowTranslationContext({ incidentWorkItem }),
          },
        },
        value: option.value,
      })),
    isVisible: ({ formValues }) =>
      match(formValues.didYouLeave?.value)
        .with("yes", () => true)
        .with(undefined, "no", () => false)
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "leaveReasonOther",
    type: "textbox",
    label: tk("leavePlaceOfSafety.leaveReasonOther.title"),
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    multiline: true,
    rows: "5",
    isVisible: ({ formValues }) =>
      match(formValues.leaveReason?.value)
        .with("other", () => true)
        .with(
          undefined,
          "moreAppropriatePosAvailable",
          "unreasonableDelay",
          "visitAccidentAndEmergencyOnMedicalGrounds",
          "physicalHealthCheckComplete",
          () => false,
        )
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "leaveAuthorisedBy",
    type: "dropdown",
    label: ({ incidentWorkItem }) => ({
      translationKey: tk("leavePlaceOfSafety.leaveAuthorisedBy.title"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    requiredLabel: ({ incidentWorkItem }) => ({
      translationKey: tk("leavePlaceOfSafety.leaveAuthorisedBy.required"),
      translationOptions: {
        context: getWorkflowTranslationContext({ incidentWorkItem }),
      },
    }),
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "constable" as const,
          "mentalHealthProfessional" as const,
          "constableDelegate" as const,
          "mentalHealthProfessionalDelegate" as const,
          "noAgreement" as const,
          "other" as const,
        ]),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk("leavePlaceOfSafety.leaveAuthorisedBy.options.constable"),
        value: "constable",
      },
      {
        label: tk(
          "leavePlaceOfSafety.leaveAuthorisedBy.options.mentalHealthProfessional",
        ),
        value: "mentalHealthProfessional",
      },
      {
        label: tk(
          "leavePlaceOfSafety.leaveAuthorisedBy.options.constableDelegate",
        ),
        value: "constableDelegate",
      },
      {
        label: tk(
          "leavePlaceOfSafety.leaveAuthorisedBy.options.mentalHealthProfessionalDelegate",
        ),
        value: "mentalHealthProfessionalDelegate",
      },
      {
        label: tk("leavePlaceOfSafety.leaveAuthorisedBy.options.noAgreement"),
        value: "noAgreement",
      },
      {
        label: tk("leavePlaceOfSafety.leaveAuthorisedBy.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      match(formValues.leaveReason?.value)
        .with(
          "moreAppropriatePosAvailable",
          "unreasonableDelay",
          "visitAccidentAndEmergencyOnMedicalGrounds",
          "physicalHealthCheckComplete",
          "other",
          () => true,
        )
        .with(undefined, () => false)
        .exhaustive(),
  })
  .withFieldConditionallyVisible({
    fieldName: "leaveAuthorisedByOther",
    type: "textbox",
    label: tk("leavePlaceOfSafety.leaveAuthorisedByOther.title"),
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      match(formValues.leaveAuthorisedBy?.value)
        .with("other", () => true)
        .with(
          undefined,
          "constable",
          "mentalHealthProfessional",
          "constableDelegate",
          "mentalHealthProfessionalDelegate",
          "noAgreement",
          () => false,
        )
        .exhaustive(),
  })
  .withPage(["didYouLeave"])
  .withPage(["leaveReason", "leaveReasonOther"])
  .withPage(["leaveAuthorisedBy", "leaveAuthorisedByOther"])
  .build({ t });

export type LeavePlaceOfSafetyFormData = ReturnType<
  typeof formSpecification
>["formType"];
